// SPIDER Viewer host<->viewer wire protocol (v2).
//
// This is the CANONICAL contract for the WebRTC data-channel messages exchanged
// between the desktop-agent (host) and the web-portal Viewer. The host is a
// plain-JS renderer and can't import this file, so it keeps a hand-maintained
// runtime mirror at apps/desktop-agent/src/renderer/protocol.js (window.SPIDER_PROTO)
// plus these same shapes inline. When you change a message shape, change it in
// BOTH places.
//
// Discriminator conventions are strict and must not be mixed:
//   - t-field   : high-frequency input/cursor streams ("input", "input-move", "cursor")
//   - op-field  : reliable control/clipboard/file/chat commands ("control", ...)
//   - type-field: the signaling envelope (register/join/offer/answer/ice/...)
//
// Every v2 addition is ADDITIVE and capability-gated: an older peer ignores an
// unknown op (else-branch fall-through) or an unconsumed data channel, so a
// portal-only or agent-only deploy degrades gracefully (see the v2 skew matrix).

export const PROTOCOL_VERSION = 2;

// ---- cursor channel (agent -> viewer, unreliable; label "cursor") ----
// One host->viewer sample of the real OS pointer, normalized 0..1 to the CAPTURED
// display's logical bounds. `seq` is a monotonic wrapping uint32 the viewer uses to
// drop stale/reordered packets (the channel is ordered:false,maxRetransmits:0).
export type CursorShape =
  | "default"
  | "text"
  | "hand"
  | "busy"
  | "progress"
  | "crosshair"
  | "move"
  | "ew-resize"
  | "ns-resize"
  | "nwse-resize"
  | "nesw-resize"
  | "not-allowed";

export interface CursorSample {
  t: "cursor";
  seq: number; // monotonic uint32 (wraps)
  x: number; // 0..1 within captured display logical bounds
  y: number; // 0..1
  visible: boolean; // false when the pointer is on a different monitor than the captured one
  shape: CursorShape;
}

// ---- control channel ops (bidirectional; label "control") ----
export const ControlOp = {
  // agent -> viewer
  SOURCES: "sources", // enriched monitor/window list
  QUALITY: "quality",
  PERMS: "perms",
  HOST_STATE: "host-state", // { locked }
  BLANK_STATE: "blank-state",
  HOST_CAPS: "host-caps", // v2: single union capability snapshot
  REBOOTING: "rebooting", // v2: host is about to reboot
  // The SECURE DESKTOP (UAC consent, lock screen, Ctrl+Alt+Del screen) took over
  // input. Normal capture cannot see it, so the video freezes; while this is on
  // the viewer renders the stills arriving on the "secure" data channel instead.
  SECURE_DESKTOP: "secure-desktop", // { on }
  // viewer -> agent
  // Lift every key/button the host still holds — sent when the viewer loses
  // keyboard focus mid-chord, so a modifier can't stay latched on the host.
  RELEASE_KEYS: "release-keys",
  LIST_SOURCES: "list-sources",
  SWITCH_SOURCE: "switch-source",
  KEYCOMBO: "keycombo",
  BLANK: "blank",
  CURSOR_OVERLAY: "cursor-overlay", // v2: viewer announces it renders the remote cursor overlay
  REBOOT: "reboot" // v2: viewer requests a host reboot
} as const;

// host-caps: presence of this message signals a v2-capable agent. The viewer gates
// the Reboot button and monitor picker on it.
export interface HostCaps {
  op: "host-caps";
  v: number; // PROTOCOL_VERSION the host speaks
  platform: "windows" | "macos" | "linux";
  build: string; // AGENT_BUILD string
  canReboot: boolean;
  canInject: boolean;
  canBlank: boolean;
  canFiles: boolean;
  displayCount: number;
  autologon: boolean; // auto sign-in configured (reboot returns unattended)
  fixedPw: boolean; // a fixed unattended password is set (reconnect survives reboot)
  // Elevated input service reachable: input also lands on UAC prompts and
  // "run as administrator" windows, and Ctrl+Alt+Del is the real SAS. Absent on
  // older hosts, which is indistinguishable from "not installed" — treat as false.
  elevated?: boolean;
}

// enriched "sources" (back-compatible: each entry still carries id+name so an old
// viewer that only reads {id,name} keeps working).
export type SourceKind = "screen" | "window" | "all" | "test";
export interface RemoteSource {
  id: string;
  name: string;
  label?: string;
  kind?: SourceKind;
  displayId?: string | null;
  primary?: boolean;
}
export interface SourcesMessage {
  op: "sources";
  current: string;
  sources: RemoteSource[];
}

// The __all_monitors__ sentinel source id (host composites every display into one
// stream). Kept in sync with the agent's ALL_MONITORS_ID.
export const ALL_MONITORS_ID = "__all_monitors__";

// sessionStorage key the viewer uses to carry a server-minted reconnect token
// across the navigate() that re-dials a rebooted host.
export const RECONNECT_TOKEN_KEY = "spider_reconnect_token";
